import type { DestroyOptions } from 'pixi.js';
import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import type { EditorTrayModel } from './EditorTrayModel';
import { HorizontalScrollBar } from '../../game/views/common/HorizontalScrollBar';
import { EditorTrayStackView } from './EditorTrayStackView';
import MathUtils from '../../utils/MathUtils';
import { registerResizableObject } from '../../utils/pixi/registerResizableObject';
import { TILE_HEIGHT } from '../../game/boardMVC/views/board/TileView';
import type { App } from '../../app/App';

const scrollBarPadding = 40;

export class EditorTrayView extends Container {
  private readonly stacks = new Container().props({
    parent: this,
  });

  private readonly scrollbar: HorizontalScrollBar;

  public readonly onStackPointerDown = new Signal<(stackView: EditorTrayStackView) => void>();

  constructor(
    private readonly app: App,
    private readonly model: EditorTrayModel,
  ) {
    super();

    const scrollBarWidth = this.app.renderer.width - scrollBarPadding * 2;

    this.scrollbar = new HorizontalScrollBar({
      stage: this.pixi.stage,
      width: scrollBarWidth,
      height: 25,
      backgroundColor: 0xeeeeee,
      knobColor: 0xff0000,
      cornerRadius: 10,
      onScrolled: (): void => {
        this.updateContentPosition();
      },
    }).props({
      parent: this,
      y: TILE_HEIGHT / 2,
      x: scrollBarPadding,
    });

    this.initTileStackViews();

    this.scrollbar.resizeKnob(MathUtils.clamp(scrollBarWidth / this.stacks.width, 0, 1));
    registerResizableObject(this);
  }

  private initTileStackViews(): void {
    this.model.getStacks().forEach((stackModel, index) => {
      const stackView = new EditorTrayStackView(stackModel).props({
        parent: this.stacks,
        y: 0,
        x: this.stacks.width + scrollBarPadding,
      });

      stackView.setLabelText(index.toString());

      stackView.onStackPointerDown.connect(() => {
        this.onStackPointerDown.emit(stackView);
      });

      return stackView;
    });
  }

  private updateContentPosition(): void {
    const scrollWidth = this.stacks.width - this.scrollbar.width;
    this.stacks.x = -scrollWidth * this.scrollbar.getKnobPosition();
  }

  public getModel(): EditorTrayModel {
    return this.model;
  }

  public resize(width: number): void {
    const scrollBarWidth = width - scrollBarPadding * 2;
    this.scrollbar.resize(scrollBarWidth);
    this.scrollbar.resizeKnob(MathUtils.clamp(scrollBarWidth / this.stacks.width, 0, 1));
    this.updateContentPosition();
  }

  public destroy(options?: DestroyOptions): void {
    this.onStackPointerDown.disconnectAll();
    super.destroy(options);
  }

  public get stacksInteractive(): boolean {
    return this.stacks.interactiveChildren ?? true;
  }

  public set stacksInteractive(value: boolean) {
    this.stacks.interactiveChildren = value;
  }
}
